import * as THREE from 'three';
import { GUARDIAN_POSITION } from './guardianScene';
import { getGuardianCounterAttack } from './sectorEnemies';
import { createRegularEnemyAttackScene } from './regularEnemyAttackScene';

// Counterattacks share the board's world so the charge, blades, trails, and
// impact stay connected as the camera turns from the enemy back to the sling.
export const createGuardianCounterScene = (scene) => {
  const regularAttack = createRegularEnemyAttackScene(scene);
  const effect = new THREE.Group();
  effect.name = 'warden-rift-counterattack';
  effect.visible = false;
  scene.add(effect);
  const charge = new THREE.Group();
  const volley = new THREE.Group();
  const impact = new THREE.Group();
  charge.name = 'warden-rift-charge';
  volley.name = 'warden-rift-volley';
  impact.name = 'warden-rift-impact';
  effect.add(charge, volley, impact);
  const glow = new THREE.MeshBasicMaterial({
    color: '#ffb66d',
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    toneMapped: false,
  });
  const shell = new THREE.MeshStandardMaterial({
    color: '#693b75',
    emissive: '#ffb66d',
    emissiveIntensity: 1.1,
    metalness: 0.6,
    roughness: 0.25,
    flatShading: true,
  });
  const white = glow.clone();
  white.color.set('#fff2df');
  const violet = glow.clone();
  violet.color.set('#b183ff');

  // Reuse a few meshes for the counter-rotating gate and its radial circuitry.
  const ringGeometry = new THREE.RingGeometry(
    0.91,
    1,
    48,
    1,
    0.12,
    Math.PI * 1.8
  );
  const gateRings = [violet, glow, white].map((surface) => {
    const ring = new THREE.Mesh(ringGeometry, surface);
    ring.material.side = THREE.DoubleSide;
    charge.add(ring);
    return ring;
  });
  const sigilPoints = [];
  for (let index = 0; index < 24; index += 1) {
    const angle = (index / 24) * Math.PI * 2;
    for (const radius of [index % 3 === 0 ? 2.4 : 2.65, 2.95])
      sigilPoints.push(
        new THREE.Vector3(
          Math.cos(angle) * radius,
          Math.sin(angle) * radius,
          0.08
        )
      );
  }
  const sigil = new THREE.LineSegments(
    new THREE.BufferGeometry().setFromPoints(sigilPoints),
    new THREE.LineBasicMaterial({
      color: '#f4d4ff',
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      toneMapped: false,
    })
  );
  charge.add(sigil);
  const chargeHeart = new THREE.Mesh(
    new THREE.IcosahedronGeometry(0.32, 1),
    white
  );
  charge.add(chargeHeart);

  const riftShape = new THREE.Shape();
  riftShape.moveTo(0, 2.2);
  [
    [-0.22, 1.2],
    [-0.08, 0.75],
    [-0.35, 0.2],
    [-0.14, -0.15],
    [-0.26, -1],
    [0, -2.2],
    [0.18, -0.85],
    [0.1, -0.5],
    [0.35, 0.1],
    [0.16, 0.65],
    [0.24, 1.25],
  ].forEach(([x, y]) => riftShape.lineTo(x, y));
  riftShape.closePath();
  const rift = new THREE.Group();
  const voidMaterial = new THREE.MeshBasicMaterial({
    color: '#10051e',
    side: THREE.DoubleSide,
  });
  rift.add(new THREE.Mesh(new THREE.ShapeGeometry(riftShape), voidMaterial));
  const riftEdgeMaterial = new THREE.LineBasicMaterial({
    color: '#f8c0ff',
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    toneMapped: false,
  });
  const edgeGeometry = new THREE.BufferGeometry().setFromPoints(
    riftShape.getPoints().map(({ x, y }) => new THREE.Vector3(x, y, 0.03))
  );
  rift.add(new THREE.Line(edgeGeometry, riftEdgeMaterial));
  const riftGlow = new THREE.Mesh(new THREE.ShapeGeometry(riftShape), violet);
  riftGlow.scale.set(1.9, 1.04, 1);
  riftGlow.position.z = -0.04;
  rift.add(riftGlow);
  charge.add(rift);

  const shardGeometry = new THREE.OctahedronGeometry(0.22, 0);
  const embers = Array.from({ length: 14 }, () => {
    const ember = new THREE.Mesh(shardGeometry, glow);
    charge.add(ember);
    return ember;
  });
  const bladeShape = new THREE.Shape();
  bladeShape.moveTo(-0.85, -0.9);
  [
    [-0.18, -0.28],
    [0.14, 0.32],
    [0.25, 1.15],
    [0.72, 0.24],
    [0.43, -0.4],
    [-0.1, -0.72],
  ].forEach(([x, y]) => bladeShape.lineTo(x, y));
  bladeShape.closePath();
  const bladeGeometry = new THREE.ExtrudeGeometry(bladeShape, {
    depth: 0.16,
    bevelEnabled: false,
  });
  const bladeEdges = new THREE.EdgesGeometry(bladeGeometry);
  const blades = Array.from({ length: 7 }, () => {
    const blade = new THREE.Group();
    const fang = new THREE.Mesh(shardGeometry, shell);
    fang.scale.set(0.8, 3.2, 0.8);
    const cleave = new THREE.Mesh(bladeGeometry, shell);
    const edge = new THREE.LineSegments(bladeEdges, riftEdgeMaterial);
    cleave.add(edge);
    const core = new THREE.Mesh(shardGeometry, white);
    core.scale.set(0.3, 2.4, 0.3);
    blade.add(fang, cleave, core);
    volley.add(blade);
    return { blade, fang, cleave, core };
  });
  const trailSegments = 18;
  const trailPositions = new THREE.BufferAttribute(
    new Float32Array(7 * trailSegments * 6),
    3
  );
  const trailGeometry = new THREE.BufferGeometry();
  trailGeometry.setAttribute('position', trailPositions);
  const trailMaterial = new THREE.LineBasicMaterial({
    color: '#ffb66d',
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    toneMapped: false,
  });
  const trails = new THREE.LineSegments(trailGeometry, trailMaterial);
  trails.frustumCulled = false;
  effect.add(trails);

  const impactPositions = new THREE.BufferAttribute(
    new Float32Array(18 * 6),
    3
  );
  const impactGeometry = new THREE.BufferGeometry();
  impactGeometry.setAttribute('position', impactPositions);
  const impactMaterial = trailMaterial.clone();
  const cracks = new THREE.LineSegments(impactGeometry, impactMaterial);
  cracks.frustumCulled = false;
  impact.add(cracks);
  const fragments = Array.from({ length: 12 }, () => {
    const fragment = new THREE.Mesh(shardGeometry, glow);
    impact.add(fragment);
    return fragment;
  });
  const impactRings = [white, violet, glow].map((surface) => {
    const ring = new THREE.Mesh(ringGeometry, surface);
    impact.add(ring);
    return ring;
  });
  // Form the rift beyond the guardian's chest, clear of its solid shell.
  const origin = GUARDIAN_POSITION.clone().add(new THREE.Vector3(0, 0.3, 2.4));
  const forward = new THREE.Vector3();
  const right = new THREE.Vector3();
  const up = new THREE.Vector3();
  const worldUp = new THREE.Vector3(0, 1, 0);
  const point = new THREE.Vector3();
  const tail = new THREE.Vector3();
  const clamp = THREE.MathUtils.clamp;
  const impactDuration = 900;

  return {
    group: effect,
    projectiles: blades,
    update({ enemy, combat, elapsed, target, camera, reduceMotion }) {
      if (enemy?.shape !== 'warden') {
        effect.visible = false;
        regularAttack.update({
          enemy,
          combat,
          elapsed,
          target,
          camera,
          reduceMotion,
        });
        return;
      }
      regularAttack.group.visible = false;
      effect.visible =
        !!combat?.retaliation &&
        elapsed >= combat.counterWindupAt &&
        elapsed < combat.counterAt + impactDuration;
      if (!effect.visible) return;
      const attack = getGuardianCounterAttack(enemy, combat.attacks);
      const variant = ((combat.attacks || 1) - 1) % 3;
      const enraged = combat.enemyHpAfter <= 250;
      effect.userData.attackName = attack.name;
      const charging = elapsed < combat.counterLaunchAt;
      const traveling = !charging && elapsed < combat.counterAt;
      const chargeProgress = clamp(
        (elapsed - combat.counterWindupAt) /
          (combat.counterLaunchAt - combat.counterWindupAt),
        0,
        1
      );
      const flight = clamp(
        (elapsed - combat.counterLaunchAt) /
          (combat.counterAt - combat.counterLaunchAt),
        0,
        1
      );
      const impactProgress = clamp(
        (elapsed - combat.counterAt) / impactDuration,
        0,
        1
      );
      const time = reduceMotion ? 0 : elapsed / 1000;
      const color = combat.damage === 0 && !charging ? '#8effe4' : enemy.color;
      glow.color.set(color);
      shell.color.set(enemy.color).multiplyScalar(0.35);
      shell.emissive.set(enemy.color);
      shell.emissiveIntensity = charging ? 0.6 + chargeProgress * 1.5 : 1.6;
      glow.opacity = reduceMotion ? 0.45 : 0.7;
      white.opacity = 0.9;
      violet.opacity = 0.18 + chargeProgress * 0.24;
      riftEdgeMaterial.opacity = 0.45 + chargeProgress * 0.55;

      charge.visible = charging || (attack.rift && traveling && flight < 0.5);
      charge.position.copy(origin);
      charge.quaternion.copy(camera.quaternion);
      const gateScale = reduceMotion
        ? 0.8
        : 0.65 + chargeProgress * (enraged ? 0.7 : 0.55);
      gateRings.forEach((ring, index) => {
        ring.visible = !reduceMotion;
        ring.scale.setScalar((2.1 + index * 0.48) * gateScale);
        ring.rotation.z = time * (index % 2 ? -0.75 : 0.6) + index * 1.4;
        ring.position.z = -0.15 - index * 0.08;
      });
      sigil.visible = !reduceMotion;
      sigil.scale.setScalar(gateScale);
      sigil.rotation.z = -time * 0.24;
      sigil.material.opacity = 0.25 + chargeProgress * 0.65;
      chargeHeart.visible = !reduceMotion;
      chargeHeart.scale.setScalar(0.4 + chargeProgress * 2.4);
      chargeHeart.rotation.set(time, time * 1.4, time * 0.6);
      chargeHeart.position.z = 0.12;
      rift.visible = attack.rift;
      rift.scale.set(
        (reduceMotion ? 0.8 : 0.12 + chargeProgress * 0.95) *
          (traveling ? 1 - flight : 1) *
          1.8,
        0.5 + chargeProgress * 0.95,
        1
      );
      rift.rotation.z = reduceMotion
        ? -0.15
        : -0.15 + Math.sin(time * 3) * 0.035;
      embers.forEach((ember, index) => {
        const angle = index * 2.39996 + time * 1.1;
        const radius = (attack.rift ? 2.5 : 1.6) * (1 - chargeProgress * 0.7);
        ember.visible = !reduceMotion || index < 4;
        ember.position.set(
          Math.cos(angle) * radius,
          Math.sin(angle) * radius * (attack.rift ? 1.25 : 0.8),
          0.3
        );
        ember.rotation.set(time * 2 + index, angle, angle);
        ember.scale.set(0.4, 0.8 + chargeProgress * 0.8, 0.4);
      });

      forward.subVectors(target, origin).normalize();
      right.crossVectors(forward, worldUp).normalize();
      up.crossVectors(right, forward).normalize();
      const sampleFlight = (out, progress, index) => {
        const angle =
          (index / attack.blades) * Math.PI * 2 +
          (variant === 1 ? progress * Math.PI * 2 : progress * 0.9);
        const radius =
          (1 - progress) *
          (1.6 + Math.sin(progress * Math.PI) * (variant === 2 ? 2.2 : 1.4));
        out.lerpVectors(origin, target, progress);
        out.addScaledVector(right, Math.cos(angle) * radius);
        out.addScaledVector(
          up,
          Math.sin(angle) * radius * (variant === 2 ? 0.65 : 1)
        );
      };
      volley.visible = charging || traveling;
      trails.visible = traveling && !reduceMotion;
      trailMaterial.color.set(enemy.color);
      trailMaterial.opacity = 0.85;
      blades.forEach(({ blade, fang, cleave, core }, index) => {
        blade.visible = index < attack.blades && (!reduceMotion || charging);
        if (!blade.visible) {
          for (let segment = 0; segment < trailSegments * 2; segment += 1)
            trailPositions.setXYZ(index * trailSegments * 2 + segment, 0, 0, 0);
          return;
        }
        const angle =
          (index / attack.blades) * Math.PI * 2 +
          time * (charging ? 0.5 : 0.18);
        sampleFlight(point, flight, index);
        blade.position.copy(point);
        blade.quaternion.copy(camera.quaternion);
        blade.rotateZ(angle + (attack.rift ? -0.4 : Math.PI / 4));
        blade.scale.setScalar((0.85 + flight * 0.6) * (enraged ? 1.15 : 1));
        fang.visible = !attack.rift;
        cleave.visible = attack.rift;
        core.visible = true;
        for (let segment = 0; segment < trailSegments; segment += 1) {
          const progress = Math.max(
            0,
            flight - (segment / trailSegments) * 0.26
          );
          sampleFlight(point, progress, index);
          sampleFlight(
            tail,
            Math.max(0, progress - 0.26 / trailSegments),
            index
          );
          const vertex = (index * trailSegments + segment) * 2;
          trailPositions.setXYZ(vertex, point.x, point.y, point.z);
          trailPositions.setXYZ(vertex + 1, tail.x, tail.y, tail.z);
        }
      });
      trailPositions.needsUpdate = true;

      impact.visible = elapsed >= combat.counterAt && impactProgress < 1;
      if (impact.visible) {
        impact.position.copy(target);
        impact.quaternion.copy(camera.quaternion);
        impactMaterial.color.set(color);
        impactMaterial.opacity = (1 - impactProgress) * 0.95;
        glow.opacity = (1 - impactProgress) * (reduceMotion ? 0.25 : 0.8);
        white.opacity = (1 - impactProgress) * (reduceMotion ? 0.25 : 0.85);
        violet.opacity = (1 - impactProgress) * 0.65;
        impactRings.forEach((ring, index) => {
          ring.visible = !reduceMotion || index === 0;
          const burst = clamp((impactProgress - index * 0.08) / 0.84, 0, 1);
          const radius = reduceMotion ? 1.1 : 0.6 + burst * (3.8 + index * 1.2);
          ring.scale.set(radius, radius * (index === 1 ? 0.4 : 0.85), 1);
          ring.rotation.z = index * 1.3 + (reduceMotion ? 0 : burst * 0.8);
        });
        const spread = reduceMotion
          ? 1
          : 0.35 + impactProgress * (attack.rift ? 4 : 2.6);
        for (let index = 0; index < 18; index += 1) {
          const angle = index * 2.39996;
          const length = spread * (0.6 + (index % 3) * 0.25);
          impactPositions.setXYZ(
            index * 2,
            Math.cos(angle) * length * 0.3,
            Math.sin(angle) * length * 0.3,
            0
          );
          impactPositions.setXYZ(
            index * 2 + 1,
            Math.cos(angle + 0.12) * length,
            Math.sin(angle + 0.12) * length,
            0
          );
        }
        impactPositions.needsUpdate = true;
        fragments.forEach((fragment, index) => {
          const angle = index * 2.39996;
          fragment.visible = !reduceMotion;
          fragment.position.set(
            Math.cos(angle) * spread,
            Math.sin(angle) * spread,
            0.2
          );
          fragment.rotation.set(time * 3, angle, time * 4);
          fragment.scale.setScalar((1 - impactProgress) * 0.5);
        });
      }
    },
    dispose() {
      regularAttack.dispose();
      const geometries = new Set();
      const materials = new Set();
      effect.traverse((object) => {
        if (object.geometry) geometries.add(object.geometry);
        if (object.material) materials.add(object.material);
      });
      scene.remove(effect);
      geometries.forEach((geometry) => geometry.dispose());
      materials.forEach((material) => material.dispose());
    },
  };
};
